import { computed, ref } from 'vue'
import api from '../../axios-config.js'

export type FeatureFlags = {
  appAccess: boolean
  marketingKitGeneration: boolean
  imageScraping: boolean
  perSlideOverrides: boolean
  aiChatAssistant: boolean
  enrichment: boolean
  outreachEngine: boolean
  governancePack: boolean
  includedOutreachEmails?: number | null
  includedAiCredits?: number | null
}

export type Entitlements = {
  status: string
  tier: string | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  billing_interval: string | null
  features: FeatureFlags
  access_mode?: string | null
  days_until_period_end?: number | null
  membership_role?: string | null
  total_seats?: number | null
  used_seats?: number | null
  can_invite?: boolean
  /**
   * Phase D3 — per-org rollout flags served verbatim from
   * organizations.feature_flag_json. Separate from `features` so we can
   * distinguish plan-tier capability from an opt-in beta gate.
   */
  feature_flags?: Record<string, boolean | number | string>
}

type FeatureFlagsApi = FeatureFlags & {
  app_access?: boolean
  marketing_kit_generation?: boolean
  image_scraping?: boolean
  per_slide_overrides?: boolean
  ai_chat_assistant?: boolean
  outreach_engine?: boolean
}

type EntitlementsApi = Partial<Entitlements> & {
  appAccess?: boolean
  app_access?: boolean
  hasAppAccess?: boolean
  has_app_access?: boolean
  features?: FeatureFlagsApi
  feature_flags?: FeatureFlagsApi
  flags?: FeatureFlagsApi
}

function normalizeFeatures(rawFeatures: FeatureFlagsApi | null | undefined): FeatureFlags {
  return {
    appAccess: Boolean(rawFeatures?.appAccess ?? rawFeatures?.app_access),
    marketingKitGeneration: Boolean(rawFeatures?.marketingKitGeneration ?? rawFeatures?.marketing_kit_generation),
    imageScraping: Boolean(rawFeatures?.imageScraping ?? rawFeatures?.image_scraping),
    perSlideOverrides: Boolean(rawFeatures?.perSlideOverrides ?? rawFeatures?.per_slide_overrides),
    aiChatAssistant: Boolean(rawFeatures?.aiChatAssistant ?? rawFeatures?.ai_chat_assistant),
    enrichment: Boolean(rawFeatures?.enrichment),
    outreachEngine: Boolean(rawFeatures?.outreachEngine ?? rawFeatures?.outreach_engine),
    governancePack: Boolean((rawFeatures as any)?.governancePack ?? (rawFeatures as any)?.governance_pack),
    includedOutreachEmails: (rawFeatures as any)?.includedOutreachEmails ?? (rawFeatures as any)?.included_outreach_emails ?? null,
    includedAiCredits: (rawFeatures as any)?.includedAiCredits ?? (rawFeatures as any)?.included_ai_credits ?? null,
  }
}

function normalizeEntitlements(raw: Entitlements | null | undefined): Entitlements | null {
  if (!raw) return null

  const payload = (raw as any)?.entitlements ?? (raw as any)?.data?.entitlements ?? (raw as any)?.data ?? raw
  if (!payload) return null
  const entitlementPayload = payload as EntitlementsApi

  const normalizedFeatures = normalizeFeatures(
    entitlementPayload.features ?? entitlementPayload.feature_flags ?? entitlementPayload.flags
  )

  const status = String(entitlementPayload.status ?? '').toLowerCase()
  const accessMode = String((entitlementPayload as any).access_mode ?? '').toLowerCase()
  const hasStatusBackedAccess =
    (status === 'active' || status === 'trialing') &&
    (accessMode === '' || ['full', 'limited', 'grace'].includes(accessMode))
  const hasExplicitAccessFlag = Boolean(
    entitlementPayload.appAccess ??
    entitlementPayload.app_access ??
    entitlementPayload.hasAppAccess ??
    entitlementPayload.has_app_access
  )

  return {
    status: (entitlementPayload.status as string) ?? 'unknown',
    tier: (entitlementPayload.tier as string | null) ?? null,
    current_period_end: (entitlementPayload.current_period_end as string | null) ?? null,
    cancel_at_period_end: Boolean(entitlementPayload.cancel_at_period_end),
    billing_interval: (entitlementPayload.billing_interval as string | null) ?? null,
    access_mode: (entitlementPayload as any).access_mode ?? null,
    days_until_period_end: (entitlementPayload as any).days_until_period_end ?? null,
    membership_role: (entitlementPayload as any).membership_role ?? null,
    features: {
      ...normalizedFeatures,
      appAccess: normalizedFeatures.appAccess || hasExplicitAccessFlag || hasStatusBackedAccess,
    },
  }
}

const entitlements = ref<Entitlements | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
let initialized = false
let inflightFetch: Promise<Entitlements | null> | null = null

const defaultFeatures: FeatureFlags = {
  appAccess: false,
  marketingKitGeneration: false,
  imageScraping: false,
  perSlideOverrides: false,
  aiChatAssistant: false,
  enrichment: false
}

const hasAppAccess = computed(() => Boolean(entitlements.value?.features?.appAccess))
const tier = computed(() => entitlements.value?.tier || null)
const features = computed(() => entitlements.value?.features || defaultFeatures)

const fetchEntitlements = async (force = false) => {
  if (loading.value && inflightFetch) return inflightFetch
  if (!force && entitlements.value && initialized) {
    return entitlements.value
  }
  loading.value = true
  error.value = null
  inflightFetch = (async () => {
    try {
      const response = await api.get<Entitlements>('/api/me/entitlements', {
        params: { refresh: true },
        paramsSerializer: (params) => new URLSearchParams(params as Record<string, string>).toString()
      })
      entitlements.value = normalizeEntitlements(response.data)
      initialized = true
    } catch (err: any) {
      if (err?.response?.status === 401) {
        entitlements.value = null
      } else {
        error.value = 'Unable to load subscription status.'
      }
    } finally {
      loading.value = false
      inflightFetch = null
    }
    return entitlements.value
  })()

  return inflightFetch
}

const resetEntitlements = () => {
  entitlements.value = null
  initialized = false
  inflightFetch = null
}

export const entitlementsStore = {
  entitlements,
  loading,
  error,
  fetchEntitlements,
  hasAppAccess,
  tier,
  features,
  resetEntitlements
}

export function useEntitlements() {
  return entitlementsStore
}

export default useEntitlements
